import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import refinanceQuestions from "../../data/refinance.json";
import purchaseQuestions from "../../data/purchase.json";
import spanishRefinanceQuestions from "../../data/spanishrefinance.json";
import spanishPurchaseQuestions from "../../data/spanishpurchase.json";
import Questionnaire from "./Questionnaire";
import axios from "axios";
import { API_URL } from "../../constants";
import {
  setApplicationId,
  setCurrentStep,
  setSessionId,
  setCrmLeadId,
} from "../../redux/actions";
import RejectMessage from "../RejectMessage/RejectMessage";
import {
  createApplication,
  getSessionId,
  saveStepdata,
  crmLeadCreate,
} from "../../services/formService";
import { extractUrlParameters, preserveAllUrlParams, restoreDynamicParamsFromStorage } from "../../utils/urlParams";
import "./Loader.css"; 

const DynamicForm = () => {
  const formType = useSelector((state) => state.formType);
  const formData = useSelector((state) => state.formData);
  const currentStep = useSelector((state) => state.currentStep);
  const sessionId = useSelector((state) => state?.sessionId);
  const applicationId = useSelector((state) => state?.applicationId);

  const [questions, setQuestions] = useState([]);
  const [showRejectMessage, setShowRejectMessage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem("language");
    return saved === "Spanish" ? "Spanish" : "English";
  });
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === "language") {
        setLang(e.newValue === "Spanish" ? "Spanish" : "English");
      }
    };

    const handleLangChanged = (e) => {
      const next = e?.detail;
      setLang(next === "Spanish" ? "Spanish" : "English");
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener("langChanged", handleLangChanged);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("langChanged", handleLangChanged);
    };
  }, []);

  useEffect(() => {
    const initializeApplication = async () => {
      if (formType && !applicationId) {
        try {
          const searchParams = new URLSearchParams(location.search);
          const { registeredParams, dynamicParameters } = extractUrlParameters(searchParams);
          
          const larFromUrl = registeredParams.lar || searchParams.get("lar");
          const sourceFromUrl = registeredParams.utm_source || searchParams.get("utm_source");
          const kwFromUrl = registeredParams.utm_term || searchParams.get("utm_term");
          const campaignFromUrl = registeredParams.utm_campaign || searchParams.get("utm_campaign");

          const lar = larFromUrl || localStorage.getItem("lar") || "undefined";
          const source =
            sourceFromUrl || localStorage.getItem("utm_source") || "undefined";

          if (larFromUrl) {
            localStorage.setItem("lar", larFromUrl);
          }
          if (sourceFromUrl) {
            localStorage.setItem("utm_source", sourceFromUrl);
          }
          if (kwFromUrl) {
            localStorage.setItem("utm_term", kwFromUrl);
          }
          if (campaignFromUrl) {
            localStorage.setItem("utm_campaign", campaignFromUrl);
          }
          
          // Store dynamic parameters in localStorage
          if (dynamicParameters && dynamicParameters.length > 0) {
            localStorage.setItem("dynamic_parameters", JSON.stringify(dynamicParameters));
          }
          
          const kw = kwFromUrl || localStorage.getItem("utm_term") || "";
          const campaign = campaignFromUrl || localStorage.getItem("utm_campaign") || "";
          const language = localStorage.getItem("language") || "English";
          const {
            application_Id: newappId,
            session_Id: newSessionId,
          } = await createApplication(sessionId, formType, lar, source, kw, campaign, language, dynamicParameters);

          dispatch(setApplicationId(newappId));

          if (
            (sessionId === "undefined" || sessionId === "null") &&
            newSessionId
          ) {
            dispatch(setSessionId(newSessionId));
          }
        } catch (error) {
          console.error("Failed to create application:", error);
        }
      }
    };

    initializeApplication();
  }, [formType]);

  useEffect(() => {
    const sourceByLang = {
      English: {
        purchase: purchaseQuestions,
        refinance: refinanceQuestions,
      },
      Spanish: {
        purchase: spanishPurchaseQuestions,
        refinance: spanishRefinanceQuestions,
      },
    };

    const source = sourceByLang[lang]?.[formType] || [];
    setQuestions(source);
  }, [formType, lang]);

  const convertedJson = (formData) => {
    const newJson = {};

    for (const [key, value] of Object.entries(formData)) {
      if (Array.isArray(value)) {
        newJson[key] = value.join(", ");
      } else {
        newJson[key] = value;
      }
    }

    return newJson;
  };

  const handleFormSubmit = async () => {
    if (formData.creditInquiryAuthorization === "No") {
      setShowRejectMessage(true);
      return;
    }
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      const formattedFormData = convertedJson(formData);
      
      // Add tracking parameters to form data
      const utm_term = localStorage.getItem("utm_term") || "";
      
      if (utm_term) {
        formattedFormData.keyword = utm_term;
      }

      // Extract lar value from URL params or localStorage
      const searchParams = new URLSearchParams(location.search);
      const { registeredParams } = extractUrlParameters(searchParams);
      const { dynamicParameters } = extractUrlParameters(searchParams);
      const larFromUrl = registeredParams.lar || searchParams.get("lar");
      const lar = larFromUrl || localStorage.getItem("lar") || "";
      const dynamic_parameters=dynamicParameters
      // Call CRM lead create service
      const responseData = await crmLeadCreate(
        formattedFormData,
        formType,
        sessionId,
        applicationId,
        lar,
        dynamic_parameters
      );

      if (responseData) {
        const crmLeadId = responseData?.crmId;

        try {
          const searchParams = new URLSearchParams(location.search);
          const { dynamicParameters } = extractUrlParameters(searchParams);
          
          const finalStepData = {
            session_id: sessionId,
            application_id: applicationId,
            current_step: currentStep + 1,
            form_data: formData,
            status: "submitted",
            form_type:formType,
            crm_lead_id: crmLeadId || "",
            language: lang,
            utm_source: localStorage.getItem("utm_source") || "direct",
            utm_term,
            utm_campaign: localStorage.getItem("utm_campaign") || "",
            dynamic_parameters: dynamicParameters,
          };
         
          await saveStepdata(finalStepData);
        } catch (saveError) {
          console.error("Error saving final step with CRM ID:", saveError);
        }

        // dispatch(setCrmLeadId(crmLeadId));
        
        // Preserve all URL parameters (both registered and dynamic)
        const searchParams = preserveAllUrlParams(location.search);
        
        // Restore dynamic parameters from localStorage if not in URL
        restoreDynamicParamsFromStorage(searchParams);
        
        // Ensure required parameters have defaults if missing
        if (!searchParams.has("lar")) {
          const larFromStorage = localStorage.getItem("lar") || "admin";
          searchParams.set("lar", larFromStorage);
        }
        if (!searchParams.has("utm_source")) {
          const sourceFromStorage = localStorage.getItem("utm_source") || "direct";
          searchParams.set("utm_source", sourceFromStorage);
        }
        
        // Handle utm_term from localStorage if not in URL
        if (!searchParams.has("utm_term")) {
          const kwFromStorage = localStorage.getItem("utm_term");
          if (kwFromStorage) {
            searchParams.set("utm_term", kwFromStorage);
          }
        }
        
        // Handle utm_campaign from localStorage if not in URL
        if (!searchParams.has("utm_campaign")) {
          const campaignFromStorage = localStorage.getItem("utm_campaign");
          if (campaignFromStorage) {
            searchParams.set("utm_campaign", campaignFromStorage);
          }
        }
        
        // Handle utm_type from localStorage if not in URL
        if (!searchParams.has("utm_type")) {
          const utmTypeFromStorage = localStorage.getItem("utm_type");
          if (utmTypeFromStorage) {
            searchParams.set("utm_type", utmTypeFromStorage);
          }
        }
        
        navigate(
          `${process.env.REACT_APP_MEETINGINFO_URL}?${searchParams.toString()}`,
          { replace: true }
        );
      } else {
        console.error("Failed to create CRM lead: No response data");
      }
    } catch (error) {
      console.error("Error saving data:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToForm = () => {
    setShowRejectMessage(false);
    const lastSectionIndex = questions.findIndex(
      (section) => section.id === "creditAuthorization"
    );
    if (lastSectionIndex !== -1) {
      dispatch(setCurrentStep(lastSectionIndex));
    }
  };

  return (
    <div className="dynamicform-wrapper">
      {isSubmitting && (
        <div className="df-loader-overlay">
          <div className="df-loader-card">
            <div className="df-spinner" />
            <p className="df-loader-text">
              {lang === "Spanish" ? "Enviando sus datos" : "Submitting your details"}
            </p>
            <p className="df-loader-subtext">
              {lang === "Spanish" ? "Por favor espere..." : "Please wait..."}
            </p>
          </div>
        </div>
      )}

      {showRejectMessage ? (
        <RejectMessage onBack={handleBackToForm} />
      ) : (
        <Questionnaire questions={questions} onFormSubmit={handleFormSubmit} />
      )}
    </div>
  );
};

export default DynamicForm;
